import { TicketStatus } from "@garther/interfaces";
import { Avatar, Badge, Box, Button } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Calendar, CheckCircle2, Clock, MapPin, User, XCircle } from "lucide-react";

import { useCheckInTicket, useTicket } from "@/features/ticket/hooks/use-ticket-query";
import { RawLayout } from "@/shared/components/layout";

export const Route = createFileRoute("/_auth/ticket/$ticketId")({
  component: RouteComponent
});

function RouteComponent() {
  const { ticketId } = useParams({ from: "/_auth/ticket/$ticketId" });
  const { data: ticket, isLoading, error } = useTicket({ ticketId });
  const checkInMutation = useCheckInTicket();
  const { formatDate } = useI18n();

  const handleCheckIn = async () => {
    if (!ticket) return;

    try {
      await checkInMutation.mutateAsync({ ticketId: ticket.id });
      toast.success("Ticket checked in successfully!");
    } catch (error: any) {
      console.error("Check-in error:", error);
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to check in ticket. Please try again.";
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <RawLayout>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading ticket...</p>
            </div>
          </div>
        </div>
      </RawLayout>
    );
  }

  if (error || !ticket) {
    return (
      <RawLayout>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <XCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
              <h2 className="text-responsive-xl font-medium text-text-primary mb-2">Ticket not found</h2>
              <p className="text-text-secondary">
                {error?.message ||
                  "The ticket you're looking for doesn't exist or you don't have permission to view it."}
              </p>
            </div>
          </div>
        </div>
      </RawLayout>
    );
  }

  const eventDate = new Date(ticket.ticketTier.eventSession.startAt);
  const formattedDate = formatDate(eventDate, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric"
  });
  const formattedTime = formatDate(eventDate, {
    hour: "2-digit",
    minute: "2-digit"
  });

  const getStatusBadgeVariant = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.ACTIVE:
        return "default";
      case TicketStatus.USED:
        return "secondary";
      case TicketStatus.CANCELLED:
        return "outline";
      case TicketStatus.EXPIRED:
        return "outline";
      case TicketStatus.INACTIVE:
        return "secondary";
      default:
        return "secondary";
    }
  };

  const getStatusLabel = (status: TicketStatus) => {
    switch (status) {
      case TicketStatus.ACTIVE:
        return "Active";
      case TicketStatus.USED:
        return "Checked In";
      case TicketStatus.CANCELLED:
        return "Cancelled";
      case TicketStatus.EXPIRED:
        return "Expired";
      case TicketStatus.INACTIVE:
        return "Inactive";
      default:
        return status;
    }
  };

  const canCheckIn = ticket.status === TicketStatus.ACTIVE;
  const isCheckedIn = ticket.status === TicketStatus.USED;

  return (
    <RawLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-responsive-2xl font-medium text-text-primary">Ticket Preview</h1>
            <Badge variant={getStatusBadgeVariant(ticket.status)} className="text-responsive-sm">
              {getStatusLabel(ticket.status)}
            </Badge>
          </div>

          {/* Ticket Information Card */}
          <Box className="space-y-6">
            {/* User Information */}
            <div className="space-y-4">
              <h2 className="text-responsive-lg font-medium text-text-primary flex items-center gap-2">
                <User className="w-5 h-5" />
                Attendee Information
              </h2>
              <div className="flex items-center gap-4">
                <Avatar
                  src={ticket.booking.user.avatar || undefined}
                  name={ticket.booking.user.name || ticket.booking.user.email || "User"}
                  size="xl"
                />
                <div>
                  <p className="text-responsive-base font-medium text-text-primary">
                    {ticket.booking.user.name || "Guest User"}
                  </p>
                  <p className="text-responsive-sm text-text-secondary">
                    {ticket.booking.user.email || "No email"}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t border-border"></div>

            {/* Event Information */}
            <div className="space-y-4">
              <h2 className="text-responsive-lg font-medium text-text-primary">Event Information</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="text-responsive-base font-medium text-text-primary mb-1">
                    {ticket.ticketTier.eventSession.event.title}
                  </h3>
                  <p className="text-responsive-sm text-text-secondary">
                    {ticket.ticketTier.eventSession.event.host.name}
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-text-secondary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-responsive-sm text-text-secondary">Date</p>
                      <p className="text-responsive-base text-text-primary">{formattedDate}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-text-secondary mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-responsive-sm text-text-secondary">Time</p>
                      <p className="text-responsive-base text-text-primary">{formattedTime}</p>
                    </div>
                  </div>

                  {ticket.ticketTier.eventSession.event.locationId && (
                    <div className="flex items-start gap-3 sm:col-span-2">
                      <MapPin className="w-5 h-5 text-text-secondary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-responsive-sm text-text-secondary">Location</p>
                        <p className="text-responsive-base text-text-primary">
                          {ticket.ticketTier.eventSession.event.location?.address}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-border"></div>

            {/* Ticket Details */}
            <div className="space-y-4">
              <h2 className="text-responsive-lg font-medium text-text-primary">Ticket Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-responsive-sm text-text-secondary mb-1">Ticket ID</p>
                  <p className="text-responsive-sm text-text-primary font-mono">{ticket.id}</p>
                </div>
                <div>
                  <p className="text-responsive-sm text-text-secondary mb-1">Ticket Tier</p>
                  <p className="text-responsive-base text-text-primary">{ticket.ticketTier.title}</p>
                </div>
                <div>
                  <p className="text-responsive-sm text-text-secondary mb-1">Booking ID</p>
                  <p className="text-responsive-sm text-text-primary font-mono">{ticket.bookingId}</p>
                </div>
              </div>
            </div>
          </Box>

          {/* Check-in Button */}
          <div className="flex justify-center">
            {isCheckedIn ? (
              <div className="flex items-center gap-2 text-success-500">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-responsive-base font-medium">This ticket has been checked in</span>
              </div>
            ) : (
              <Button
                onClick={handleCheckIn}
                isDisabled={!canCheckIn || checkInMutation.isPending}
                className="min-w-[200px] px-6 py-3 w-full"
              >
                {checkInMutation.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Checking in...
                  </>
                ) : (
                  "Check In"
                )}
              </Button>
            )}
          </div>

          {!canCheckIn && !isCheckedIn && (
            <div className="text-center">
              <p className="text-responsive-sm text-text-secondary">
                This ticket cannot be checked in. Current status: {getStatusLabel(ticket.status)}
              </p>
            </div>
          )}
        </div>
      </div>
    </RawLayout>
  );
}
